import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

/* ─────────────────────────────────────────────────────────────
   The building's shared records.

   Most screens keep their own state, because nothing else reads it.
   These records are the exception: they cross from the resident side
   to the staff side, and the demo is only convincing if they do. A
   repair a resident asks for lands on the maintenance board, a guest
   they register appears on the front desk's list, and an alert the
   desk sends shows on every resident screen.

   There is no server behind this. Everything resets on reload, which
   is what a demo on a lobby screen wants.
   ───────────────────────────────────────────────────────────── */

/* The resident the demo is signed in as. */
export const RESIDENT = { name: 'Alex Rivera', unit: '1204' };

/* A date `offset` days from today, the way the screens print one. */
export function day(offset = 0) {
    const d = new Date();
    d.setDate(d.getDate() + offset);
    return d.toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric' });
}

/* A date input's value (yyyy-mm-dd) printed the same way as day(). */
export function printDate(value) {
    if (!value) return day(0);
    const [y, m, d] = value.split('-').map(Number);
    return new Date(y, m - 1, d).toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric' });
}

export function clock() {
    return new Date().toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
}

let seq = 1000;
export const nextId = () => ++seq;

/* ─────────────────────────────────────────────────────────────
   Access control: the doors, who may open them, and what happened.

   These live here rather than in the access screens because both sides
   act on the same doors. A resident's mobile-key unlock is a line in the
   front desk's live feed, a fob the resident reports lost is a red chip
   on the desk's credential list, and a lockdown the desk starts greys
   out the resident's door tiles.
   ───────────────────────────────────────────────────────────── */

/* How long a momentary unlock holds a door open. */
export const PULSE_MS = 5000;

/* The access groups a credential can carry. Each door belongs to one. */
export const ACCESS_GROUPS = ['Lobby', 'Garage', 'Gym', 'Pool', 'Rooftop', 'Package room', 'Service', 'Back of house'];

const RESIDENT_GROUPS = ['Lobby', 'Garage', 'Gym', 'Pool', 'Rooftop', 'Package room'];

const minutesAgo = minutes => Date.now() - minutes * 60 * 1000;

/* A date input's value for `offset` days from today. */
export function isoDay(offset = 0) {
    const d = new Date();
    d.setDate(d.getDate() + offset);
    const pad = n => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

const DOORS = [
    { id: 'main',          name: 'Main entrance',     zone: 'Level 1, Brickell Ave',  area: 'Entrances',     group: 'Lobby',         reader: 'Fob, mobile key and keypad', hardware: 'Electric strike, fail secure',  camera: 'cam-25' },
    { id: 'lobby-inner',   name: 'Lobby inner doors', zone: 'Level 1',                area: 'Entrances',     group: 'Lobby',         reader: 'Fob and mobile key',         hardware: 'Magnetic lock pair',            camera: 'cam-01' },
    { id: 'garage-gate',   name: 'Garage gate P1',    zone: 'Parking level 1',        area: 'Entrances',     group: 'Garage',        reader: 'Long-range vehicle tag',     hardware: 'Barrier gate operator',         camera: 'cam-12' },
    { id: 'garage-vest',   name: 'Garage vestibule',  zone: 'Parking level 1',        area: 'Entrances',     group: 'Garage',        reader: 'Fob and mobile key',         hardware: 'Electrified mortise lock',      camera: 'cam-12' },
    { id: 'package',       name: 'Package room',      zone: 'Level 1',                area: 'Amenities',     group: 'Package room',  reader: 'Fob, mobile key and PIN',    hardware: 'Electrified mortise lock',      camera: 'cam-02' },
    { id: 'fitness',       name: 'Fitness center',    zone: 'Level 5',                area: 'Amenities',     group: 'Gym',           reader: 'Fob and mobile key',         hardware: 'Electric strike, fail secure',  camera: null },
    { id: 'pool',          name: 'Pool deck',         zone: 'Level 6',                area: 'Amenities',     group: 'Pool',          reader: 'Fob and mobile key',         hardware: 'Gate lock with self closer',    camera: 'cam-07' },
    { id: 'rooftop',       name: 'Rooftop lounge',    zone: 'Level 42',               area: 'Amenities',     group: 'Rooftop',       reader: 'Fob and mobile key',         hardware: 'Electrified mortise lock',      camera: 'cam-23' },
    { id: 'dock',          name: 'Loading dock',      zone: 'Service level',          area: 'Back of house', group: 'Service',       reader: 'Fob and keypad',             hardware: 'Overhead door and man door',    camera: null },
    { id: 'service',       name: 'Service entrance',  zone: 'Level 2',                area: 'Back of house', group: 'Service',       reader: 'Fob and keypad',             hardware: 'Electric strike, fail secure',  camera: null },
    { id: 'stair-b',       name: 'Stair B roof door', zone: 'Roof',                   area: 'Back of house', group: 'Back of house', reader: 'Fob, alarmed exit device',   hardware: 'Delayed egress panic bar',      camera: null },
    { id: 'mechanical',    name: 'Mechanical room',   zone: 'Roof',                   area: 'Back of house', group: 'Back of house', reader: 'Fob and PIN',                hardware: 'Electrified mortise lock',      camera: 'cam-14' }
].map(door => ({ status: 'ok', since: 0, pulseUntil: 0, override: null, last: null, ...door }));

/* Two doors start the demo in a state worth looking at: the dock has
   been propped for a delivery, and a roof reader has dropped off the
   network. */
DOORS.find(d => d.id === 'dock').status = 'held';
DOORS.find(d => d.id === 'dock').since = Date.now() - 34 * 1000;
DOORS.find(d => d.id === 'mechanical').status = 'offline';
DOORS.find(d => d.id === 'mechanical').since = minutesAgo(52);

/* `open`/`close` are hours on a 24-hour clock; `holiday` is the pair
   that applies while holiday hours are on. An `unlock` schedule leaves a
   door free to push through; an `hours` schedule is when its group's
   credentials work at all. */
const SCHEDULES = [
    { id: 'lobby',   name: 'Lobby free passage',     kind: 'unlock', who: 'Free passage', doors: ['lobby-inner'],     open: 7,  close: 21, holiday: [9, 18],  enabled: true },
    { id: 'pool',    name: 'Pool deck',              kind: 'hours',  who: 'Residents',    doors: ['pool'],            open: 7,  close: 22, holiday: [8, 20],  enabled: true },
    { id: 'gym',     name: 'Fitness center',         kind: 'hours',  who: 'Residents',    doors: ['fitness'],         open: 5,  close: 23, holiday: [7, 21],  enabled: true },
    { id: 'rooftop', name: 'Rooftop lounge',         kind: 'hours',  who: 'Residents',    doors: ['rooftop'],         open: 10, close: 23, holiday: [12, 24], enabled: true },
    { id: 'dock',    name: 'Deliveries and vendors', kind: 'hours',  who: 'Vendors',      doors: ['dock', 'service'], open: 8,  close: 17, holiday: [9, 13],  enabled: true }
];

const CREDENTIALS = [
    { id: 1,  name: 'Alex Rivera',          unit: '1204',        type: 'Resident', medium: 'Fob',        number: '4410', groups: RESIDENT_GROUPS, status: 'Active' },
    { id: 2,  name: 'Alex Rivera',          unit: '1204',        type: 'Resident', medium: 'Mobile key', number: 'iPhone', groups: RESIDENT_GROUPS, status: 'Active' },
    { id: 3,  name: 'Mariana Costa',        unit: '803',         type: 'Resident', medium: 'Fob',        number: '4388', groups: RESIDENT_GROUPS, status: 'Active' },
    { id: 4,  name: 'Daniel Weiss',         unit: '1502',        type: 'Resident', medium: 'Mobile key', number: 'iPhone', groups: RESIDENT_GROUPS, status: 'Active' },
    { id: 5,  name: 'Priya Shah',           unit: '305',         type: 'Resident', medium: 'Fob',        number: '4519', groups: RESIDENT_GROUPS, status: 'Active' },
    { id: 6,  name: 'Robert Klein',         unit: '907',         type: 'Resident', medium: 'Fob',        number: '4602', groups: RESIDENT_GROUPS, status: 'Lost' },
    { id: 7,  name: 'Sofia Marin',          unit: '1801',        type: 'Resident', medium: 'Mobile key', number: 'Android', groups: RESIDENT_GROUPS, status: 'Active' },
    { id: 8,  name: 'James Okafor',         unit: '2204',        type: 'Resident', medium: 'Fob',        number: '4731', groups: ['Lobby', 'Garage', 'Gym', 'Package room'], status: 'Active' },
    { id: 9,  name: 'Elena Park',           unit: 'PH2',         type: 'Resident', medium: 'Fob',        number: '4815', groups: RESIDENT_GROUPS, status: 'Active' },
    { id: 10, name: 'Ana Lopez',            unit: 'Front desk',  type: 'Staff',    medium: 'Fob',        number: '1102', groups: ACCESS_GROUPS, status: 'Active' },
    { id: 11, name: 'Marcus Reed',          unit: 'Security',    type: 'Staff',    medium: 'Fob',        number: '1107', groups: ACCESS_GROUPS, status: 'Active' },
    { id: 12, name: 'Luis Martinez',        unit: 'Maintenance', type: 'Staff',    medium: 'Fob',        number: '1115', groups: ACCESS_GROUPS, status: 'Active' },
    { id: 13, name: 'Dana Kim',             unit: 'Maintenance', type: 'Staff',    medium: 'Mobile key', number: 'iPhone', groups: ACCESS_GROUPS, status: 'Active' },
    { id: 14, name: 'Sparkle Cleaning Co.', unit: 'Vendor',      type: 'Vendor',   medium: 'Fob',        number: '7021', groups: ['Lobby', 'Service'], status: 'Active' },
    { id: 15, name: 'Coastal AV',           unit: 'Vendor',      type: 'Vendor',   medium: 'Fob',        number: '7034', groups: ['Service', 'Rooftop'], status: 'Expired' },
    { id: 16, name: 'Brightline Electric',  unit: 'Vendor',      type: 'Vendor',   medium: 'Fob',        number: '7040', groups: ['Service', 'Back of house'], status: 'Suspended' }
];

const GUEST_CODES = [
    { id: 1, code: '482913', guest: 'Jordan Blake',      unit: '1204', dateValue: isoDay(0),  date: day(0),  window: 'Evening, 5:00 PM to 10:00 PM', uses: 'Once',    status: 'Active' },
    { id: 2, code: '318204', guest: 'Sam Chen',          unit: '1204', dateValue: isoDay(-1), date: day(-1), window: 'All day',                      uses: 'Once',    status: 'Used', usedAt: `${day(-1)}, 7:42 PM` },
    { id: 3, code: '905127', guest: 'Tessa Morgan',      unit: '1204', dateValue: isoDay(-4), date: day(-4), window: 'Afternoon, 12:00 PM to 5:00 PM', uses: 'All day', status: 'Expired' },
    { id: 4, code: '771045', guest: 'Grace Weiss',       unit: '1502', dateValue: isoDay(0),  date: day(0),  window: 'All day',                      uses: 'All day', status: 'Active' },
    { id: 5, code: '260388', guest: 'Harbor Dog Walkers', unit: '803', dateValue: isoDay(0),  date: day(0),  window: 'Afternoon, 12:00 PM to 5:00 PM', uses: 'All day', status: 'Active' }
];

/* The morning so far. `kind` is the feed filter it falls under, `tone`
   its chip colour and `label` the chip's word. */
const ACCESS_EVENTS = [
    { door: 'dock',        kind: 'alarm',   tone: 'warn',  label: 'Held open', title: 'Door held open',   who: 'Coastal Freight', via: 'Keypad', ago: 0.6 },
    { door: 'main',        kind: 'granted', tone: 'ok',    label: 'Granted',   title: 'Access granted',   who: 'Mariana Costa', via: 'Fob 4388', unit: '803', ago: 3 },
    { door: 'main',        kind: 'granted', tone: 'ok',    label: 'Visitor',   title: 'Visitor code used', who: 'Grace Weiss', via: 'Visitor code 771045', guestOf: '1502', ago: 7 },
    { door: 'pool',        kind: 'denied',  tone: 'alert', label: 'Denied',    title: 'Access denied',    who: 'Coastal AV', via: 'Fob 7034', note: 'Expired credential', ago: 11 },
    { door: 'fitness',     kind: 'granted', tone: 'ok',    label: 'Granted',   title: 'Access granted',   who: 'Sofia Marin', via: 'Mobile key', unit: '1801', ago: 16 },
    { door: 'garage-gate', kind: 'granted', tone: 'ok',    label: 'Granted',   title: 'Access granted',   who: 'Alex Rivera', via: 'Fob 4410', unit: '1204', ago: 24 },
    { door: 'service',     kind: 'granted', tone: 'ok',    label: 'Granted',   title: 'Access granted',   who: 'Sparkle Cleaning Co.', via: 'Fob 7021', ago: 31 },
    { door: 'mechanical',  kind: 'alarm',   tone: 'muted', label: 'Offline',   title: 'Reader offline',   who: 'Controller 3', via: 'Network', ago: 52 },
    { door: 'package',     kind: 'granted', tone: 'ok',    label: 'Granted',   title: 'Access granted',   who: 'Priya Shah', via: 'Fob 4519', unit: '305', ago: 64 },
    { door: 'rooftop',     kind: 'denied',  tone: 'alert', label: 'Denied',    title: 'Access denied',    who: 'James Okafor', via: 'Fob 4731', unit: '2204', note: 'No access to this door', ago: 88 },
    { door: 'fitness',     kind: 'granted', tone: 'ok',    label: 'Granted',   title: 'Access granted',   who: 'Alex Rivera', via: 'Mobile key', unit: '1204', ago: 215 },
    { door: 'main',        kind: 'granted', tone: 'ok',    label: 'Visitor',   title: 'Visitor code used', who: 'Sam Chen', via: 'Visitor code 318204', guestOf: '1204', ago: 60 * 17 },
    { door: 'pool',        kind: 'granted', tone: 'ok',    label: 'Granted',   title: 'Access granted',   who: 'Alex Rivera', via: 'Mobile key', unit: '1204', ago: 60 * 21 },
    { door: 'package',     kind: 'granted', tone: 'ok',    label: 'Granted',   title: 'Access granted',   who: 'Alex Rivera', via: 'Fob 4410', unit: '1204', ago: 60 * 26 }
].map(({ ago, ...event }, i) => ({
    id: 900 - i,
    ts: minutesAgo(ago),
    doorName: DOORS.find(d => d.id === event.door).name,
    ...event
}));

/* Each door's card shows the newest thing that happened at it. */
DOORS.forEach(door => { door.last = ACCESS_EVENTS.find(e => e.door === door.id) || null; });

/* The feed keeps this many events; a lobby screen left running all day
   must not grow without end. */
const EVENT_CAP = 160;

const SEED = {
    workOrders: [
        { id: 1, unit: '1204', title: 'Kitchen faucet dripping', category: 'Plumbing', priority: 'Normal', status: 'In progress', assignee: 'Luis M.', opened: day(-2), entry: true },
        { id: 2, unit: '803', title: 'Bedroom AC not cooling', category: 'HVAC', priority: 'High', status: 'Assigned', assignee: 'Dana K.', opened: day(-1), entry: true },
        { id: 3, unit: 'Lobby', title: 'Replace lamp in north sconce', category: 'Electrical', priority: 'Low', status: 'New', assignee: '', opened: day(0), entry: false },
        { id: 4, unit: '1502', title: 'Dishwasher not draining', category: 'Appliance', priority: 'Normal', status: 'New', assignee: '', opened: day(0), entry: true },
        { id: 5, unit: 'Garage P2', title: 'Gate arm sticking on exit', category: 'Access', priority: 'High', status: 'Done', assignee: 'Luis M.', opened: day(-4), entry: false }
    ],
    visitors: [
        { id: 1, name: 'Jordan Blake', unit: '1204', kind: 'Guest', date: day(0), note: 'Dinner, parking needed', status: 'Expected' },
        { id: 2, name: 'Sparkle Cleaning Co.', unit: '907', kind: 'Vendor', date: day(0), note: 'Weekly cleaning', status: 'Checked in' },
        { id: 3, name: 'Priya Shah', unit: '305', kind: 'Guest', date: day(0), note: '', status: 'Checked out' },
        { id: 4, name: 'Coastal AV', unit: 'Amenity floor', kind: 'Vendor', date: day(1), note: 'Theater projector service', status: 'Expected' }
    ],
    approvals: [
        { id: 1, title: 'Kitchen renovation', from: 'Unit 1502', kind: 'Renovation request', detail: 'Cabinets and countertops, 3 weeks, licensed contractor on file.', status: 'Pending' },
        { id: 2, title: 'Pool furniture replacement', from: 'Management', kind: 'Board spend', detail: '24 loungers and 8 umbrellas. Three quotes attached, lowest selected.', amount: '$18,400', status: 'Pending' },
        { id: 3, title: 'Second parking space', from: 'Unit 803', kind: 'Parking request', detail: 'Assigned space on P2 for a second vehicle.', status: 'Pending' },
        { id: 4, title: 'Lobby holiday decor', from: 'Management', kind: 'Board spend', detail: 'Seasonal decor, install and removal.', amount: '$3,250', status: 'Approved' }
    ],
    moves: [
        { id: 1, unit: '604', kind: 'Move-in', date: day(2), slot: '9:00 AM – 12:00 PM', status: 'Confirmed' },
        { id: 2, unit: '1110', kind: 'Move-out', date: day(5), slot: '1:00 PM – 4:00 PM', status: 'Pending' }
    ],
    alert: null,

    /* Access control (see above). `lockdown` is null, or when and by
       whom the building was locked down. */
    doors: DOORS,
    accessEvents: ACCESS_EVENTS,
    credentials: CREDENTIALS,
    guestCodes: GUEST_CODES,
    schedules: SCHEDULES,
    holidayHours: false,
    lockdown: null
};

/* An access event as the feed stores it: stamped, named, and filed under
   the door it happened at. */
function stamp(event, doors) {
    const door = event.door ? doors.find(d => d.id === event.door) : null;
    return { id: nextId(), ts: Date.now(), kind: 'system', tone: 'muted', label: 'System', doorName: door ? door.name : '', ...event };
}

/* Prepends an event to the feed and, unless it is `quiet`, makes it the
   door's latest. `changes` patch that door in the same update. */
function withEvent(r, event, changes) {
    const entry = stamp(event, r.doors);
    return {
        ...r,
        accessEvents: [entry, ...r.accessEvents].slice(0, EVENT_CAP),
        doors: entry.door
            ? r.doors.map(d => (d.id === entry.door ? { ...d, ...changes, last: entry.quiet ? d.last : entry } : d))
            : r.doors
    };
}

const BuildingContext = createContext(null);

export function BuildingProvider({ go, children }) {
    const [records, setRecords] = useState(SEED);

    /* One writer per collection: prepend a record, or patch one by id. */
    const add = useCallback((key, record) => {
        setRecords(r => ({ ...r, [key]: [{ id: nextId(), ...record }, ...r[key]] }));
    }, []);

    const patch = useCallback((key, id, changes) => {
        setRecords(r => ({ ...r, [key]: r[key].map(item => (item.id === id ? { ...item, ...changes } : item)) }));
    }, []);

    const setAlert = useCallback(alert => setRecords(r => ({ ...r, alert })), []);

    /* ── Access control writers ─────────────────────────────────
       A momentary unlock relocks itself, wherever the reader has gone
       in the meantime, so its timer belongs to the provider rather than
       to the screen that started it. */
    const timers = useRef(new Set());
    useEffect(() => {
        const pending = timers.current;
        return () => { pending.forEach(clearTimeout); pending.clear(); };
    }, []);

    /* logAccess({ door, kind, tone, label, title, who, via, note }, doorChanges) */
    const logAccess = useCallback((event, changes) => {
        setRecords(r => withEvent(r, event, changes));
    }, []);

    /* Unlocks a door for PULSE_MS, logs who did it, then relocks and
       logs that too. A second pulse before the first ends extends it. */
    const pulseDoor = useCallback((doorId, event = {}) => {
        const until = Date.now() + PULSE_MS;
        setRecords(r => withEvent(r, {
            kind: 'granted', tone: 'info', label: 'Unlocked', title: 'Momentary unlock', ...event, door: doorId
        }, { pulseUntil: until }));

        const timer = setTimeout(() => {
            timers.current.delete(timer);
            setRecords(r => {
                const door = r.doors.find(d => d.id === doorId);
                if (!door || door.pulseUntil !== until) return r;
                return withEvent(r, {
                    door: doorId, kind: 'system', tone: 'muted', label: 'Relocked', title: 'Relocked automatically',
                    who: `After ${PULSE_MS / 1000} seconds`, via: 'Controller', quiet: true
                }, { pulseUntil: 0 });
            });
        }, PULSE_MS);
        timers.current.add(timer);
    }, []);

    /* Every door locks and stays locked: propped and alarmed doors are
       reset, pulses end, and the screens refuse unlocks until it ends. */
    const startLockdown = useCallback((by = 'Front desk') => {
        setRecords(r => {
            const next = withEvent(r, {
                kind: 'alarm', tone: 'alert', label: 'Lockdown', title: 'Building lockdown started', who: by, via: 'Access control'
            });
            return {
                ...next,
                lockdown: { since: Date.now(), by },
                doors: next.doors.map(d => ({
                    ...d,
                    pulseUntil: 0,
                    ...(d.status === 'held' || d.status === 'forced' ? { status: 'ok', since: 0 } : null)
                }))
            };
        });
    }, []);

    const endLockdown = useCallback((by = 'Front desk') => {
        setRecords(r => ({
            ...withEvent(r, { kind: 'system', tone: 'ok', label: 'Restored', title: 'Lockdown ended, doors back on schedule', who: by, via: 'Access control' }),
            lockdown: null
        }));
    }, []);

    const setHolidayHours = useCallback(on => {
        setRecords(r => ({
            ...withEvent(r, { kind: 'system', tone: 'info', label: 'Schedule', title: on ? 'Holiday hours on' : 'Holiday hours off', who: 'Front desk', via: 'Schedules' }),
            holidayHours: on
        }));
    }, []);

    const value = useMemo(
        () => ({
            ...records, add, patch, setAlert, go: go || (() => {}),
            logAccess, pulseDoor, startLockdown, endLockdown, setHolidayHours
        }),
        [records, add, patch, setAlert, go, logAccess, pulseDoor, startLockdown, endLockdown, setHolidayHours]
    );

    return <BuildingContext.Provider value={value}>{children}</BuildingContext.Provider>;
}

/* Screens rendered outside a provider (a test mounting one on its own)
   still get the seed records and no-op writers rather than a crash. */
const noop = () => {};
const FALLBACK = {
    ...SEED, add: noop, patch: noop, setAlert: noop, go: noop,
    logAccess: noop, pulseDoor: noop, startLockdown: noop, endLockdown: noop, setHolidayHours: noop
};

export function useBuilding() {
    return useContext(BuildingContext) || FALLBACK;
}
